import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/useToast"
import { Loader2, Users, Sparkles } from "lucide-react"
import { submitBetaForm } from "@/api/forms"
import * as CompanyEmailValidator from "company-email-validator"


const formSchema = z.object({
  email: z
    .string()
    .email("Invalid email address")
    .refine(
      CompanyEmailValidator.isCompanyEmail,
      "Please use a corporate (non-free) email address"
    ),
  jobRole:          z.string().min(1, "Job role is required"),
  organizationName: z.string().min(1, "Organization name is required"),
  monthlyBudget:    z.string().min(1, "Monthly budget range is required"),
  challenge:        z.string().min(10, "Please describe the challenge in at least 10 characters"),
  stageid:          z.string().default("Beta Requested")
})

type FormData = z.infer<typeof formSchema>

export function BetaPage() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      jobRole: "",
      organizationName: "",
      monthlyBudget: "",
      challenge: "",
      stageid: "5", // Default stage ID
    }
  })

  const onSubmit = async (data: FormData) => {
    console.log("Submitting payload:", data)
    setIsSubmitting(true)
    try {
      const result = await submitBetaForm(data)
      
      toast({
        title: "Beta Request Submitted!",
        description: result.warning || "Thank you for your interest. We'll be in touch soon.",
        variant: result.warning ? "default" : "default",
      })
      
      if (result.warning) {
        console.warn('Beta form submission warning:', result.warning)
      }
      
      form.reset()
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
          <Users className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Join Our Beta Program
        </h1>
        <p className="text-lg text-muted-foreground">
          Be among the first to experience our revolutionary form processing platform
        </p>
      </div>
  
      {/* Form Card */}
      <Card className="glass-morphism border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            <span>Beta Access Request</span>
          </CardTitle>
          <CardDescription>
            Tell us about your organization and how you plan to use our platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* hidden stageid */}
              <input
                type="hidden"
                defaultValue={form.getValues("stageid")}
                {...form.register("stageid")}
              />
  
              {/* Email */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Corporate Email Address *</FormLabel>
                    <FormControl>
                      <Input placeholder="john@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
  
              {/* Job Role */}
              <FormField
                control={form.control}
                name="jobRole"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Role *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Marketing Manager, CTO, Developer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
  
              {/* Organization */}
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Your Company Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
  
              {/* Budget */}
              <FormField
                control={form.control}
                name="monthlyBudget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Budget Range *</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-1k">Under $1,000</SelectItem>
                          <SelectItem value="1k-5k">$1,000 - $5,000</SelectItem>
                          <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                          <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                          <SelectItem value="over-25k">Over $25,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
  
              {/* Challenge */}
              <FormField
                control={form.control}
                name="challenge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Challenge This Solves *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the specific challenge or problem our platform would solve..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
  
              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? "Submitting..." : "Request Beta Access"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
  
}